/**
 * Plan builder UI — the goal-row editor and the objective picker.
 *
 * Owns: editPlan (clone live → builder), renderPlanBuilder (the
 * autosave-backed editor with OMON-031 cosmetic-rename labels +
 * OMON-035 draft-mode rendering + OMON-108 flat SortableJS reorder),
 * renderObjectivePicker (the catalogue-search picker invoked via
 * "Add Goals..."), savePlan (write the new active CarePlan +
 * supersede priors, batched), plus per-row helpers (scoreOpts,
 * baselineOpts, monthOffsetOpts, recalcTrajectory, renderMonthCells,
 * getSmartGoalFromActivity)
 * and date helpers (monthOffsetToDate, goalStartDateStr, goalEndDateStr).
 *
 * `restoreDraftState`, `renderClientsView`, `loadPatientPlan` are
 * imported from sibling modules / main; ES module circular import is
 * fine because all references resolve inside function bodies.
 *
 * Extracted from main.ts as part of OMON-060 Phase 6b.5.
 */

import {
  collection,
  doc,
  getDoc,
  writeBatch,
  arrayUnion,
  DocumentReference,
} from "firebase/firestore";

import { planStewardshipFields } from "./plan-custodian";
import { contactName, pickOfficeContact } from "./office-contact";
import Sortable from "sortablejs";

import { state } from "../state";
import { db, appEnv } from "../firebase";
import { SHIFT_ROLES, shiftRoleDisplay, type ShiftRoleCode } from "../shift-roles";
import {
  differsFromDefault, isLocked, itemRoles, roleFields, seedRoles, setMandatoryForAll,
  carersChooseRoles, ownedByHelp, shiftRolesAvailable, toggleRole, type RoleBearing,
} from "./shift-role-policy";
import { ensureCatalogue } from "../loaders/ensure";
import { statusEl, clientsView } from "../dom";
import { escapeHtml } from "../utils/html";
import type {
  CarePlanInfo,
  ObservationDefinition,
  SelectedGoal,
  BuilderMonitoredEvent,
} from "../types";
// Ed 2026-09-29: carer guidance is ONE text; the planner edits this person's
// copy in the same one-box editor + carer preview the librarian uses.
import { guidanceEditorHtml, wireGuidanceEditor } from "../guidance/editor";
import { renderGuidanceHtml } from "../guidance/render-html";
import { composeFromLevels, parseGuidanceText, resolveGuidanceText } from "../guidance/single-text";
// OMON-135 follow-up (2026-05-24): three-layer defensive helpers that
// stop the "blank guidance after reload" bug shape. See
// `carer-guidance.ts` for the policy each helper encodes.
import {
  chooseCarerGuidanceForLoad,
  chooseCarerGuidanceForSnapshot,
  isMeaningfulOverride,
  planGuidanceOverrideOnLoad,
  planGuidanceTextToWrite,
} from "./carer-guidance";
// OMON-143 (2026-05-24): stamp FHIR R5 meta.security[HTEST] on docs
// created by `dataScope: test` users. The hierarchy.ts pattern-setter
// (OMON-111 PR #16a34a) established the helper; this is the long-tail
// roll-out for plan-builder's _carePlan + _goal batch writes.
import { metaSecurityFor } from "../admin/meta-security";

// OMON-175 (Ed, 2026-06-10): the legacy `_measurementType === "three-state-
// ordinal"` gate is retired — "deployable as a monitored event" is now the
// librarian-granted M capability (see renderMonitoredEventPicker).
import {
  goalMonths,
  ensureTrajectory,
} from "./trajectory";
import { getMonthLabelsFor, buildTrajectoryChart } from "../charts/trajectory-chart";
import type { Chart } from "chart.js";
// OMON-191: align the editable cell grid under the chart's time axis. The chart
// plots planned point i at addMonths(planStart, i); we position cell i to span
// [addMonths(planStart, i), addMonths(planStart, i+1)] using the same fn so the
// cells line up exactly under the months on the full-plan timeline above.
import { addMonths } from "date-fns";
import { resolveODHierarchy } from "../catalogue/resolvers";
import { categoryForWrite } from "../catalogue/category-code";
import { trackableCapabilities } from "../types";
import {
  builderFromPlanEntry,
  DEFAULT_ESCALATION_DIRECTION,
  DEFAULT_ESCALATION_THRESHOLD,
  defaultsFromOd,
  ensureMinimumSet,
  monitoredEntryPolicyFields,
  recordingKindShortLabel,
  requiredMonitoredOdCodes,
  seedBuilderMonitoredEvent,
} from "./monitored";
import {
  componentPromptsForSave,
  isIncidentKind,
  partsOf,
  resolvePartPrompt,
} from "./event-parts";
import { ALLOW_NOT_APPLICABLE_HELP, ALLOW_NOT_APPLICABLE_LABEL } from "./not-applicable";
import {
  restoreDraftState,
  confirmAndDiscardDraft,
  confirmAndSaveLiveVersion,
  saveDraft,
} from "./draft";
import { showToast } from "./list";
import { loadPatientPlan } from "../main";
import { renderClientsView } from "./view";
import { canPublish, isEmptyPlan } from "./publish-rules";

// ======================================================================
// Edit Existing Plan
// ======================================================================

/**
 * OMON-329: seed a new draft from a SUPERSEDED plan version.
 *
 * Restore is forward-only: it produces the NEXT version, whose content is
 * copied from an older one. Nothing is overwritten, no superseded doc is
 * resurrected, and the version chain stays whole as the audit trail. The
 * planner reviews the seeded draft and publishes it through the ordinary
 * path — this function does not publish anything.
 *
 * Deliberately built as a seed-then-reuse rather than a new code path: once
 * the chosen version's goals and monitored events are in
 * `state.patientGoals` / `state.patientMonitoredEvents`, `editPlan()` already
 * knows how to turn those into builder state (trajectories, derived fields,
 * carer-guidance policy, the OMON-174/177/183 entry-field rules). Duplicating
 * that mapping would be the surest way to reintroduce the bugs its comments
 * record.
 *
 * ☠️ REFUSES when a draft already exists. `editPlan()` resumes an in-flight
 * draft in preference to re-cloning, so calling it here would either resume
 * the wrong content or silently discard someone's unpublished work. On the
 * day this was written a colleague had 18 goals in an unpublished draft on
 * the very episode being restored.
 */
export async function restorePlanVersion(
  versionDocId: string,
): Promise<{ ok: boolean; reason?: string }> {
  if (!state.selectedPatient) return { ok: false, reason: "No client selected." };
  if (state.patientDraft) {
    // ☠️ Returned as a REASON, not just a toast. The first version of this
    // only toasted, and the caller left its button reading "Restoring…"
    // forever — so a working guard looked exactly like a hung app (Ed,
    // 2026-09-03, on the live case this guard exists for). A refusal has to
    // be visible where the action was taken, and it must not fade.
    return {
      ok: false,
      reason: "There is already a draft for this client (see the banner above)."
        + " Publish or discard it before restoring an earlier version.",
    };
  }
  const patient = state.selectedPatient;
  await loadPatientPlan(patient, { versionDocId });
  if (state.patientGoals.length === 0 && state.patientMonitoredEvents.length === 0) {
    await loadPatientPlan(patient); // put the live plan back in view
    renderClientsView();
    return {
      ok: false,
      reason: "That version has no goals or monitored events to restore.",
    };
  }
  // editPlan() clones whatever is in state into the builder. state.patientDraft
  // is null (guarded above), so it takes the clone path, not the resume path.
  // ☠️ The builder needs the full catalogue; loadPatientPlan no longer loads it.
  await ensureCatalogue();
  editPlan();
  await saveDraft();
  showToast("Restored into a draft — review it, then publish to make it live.");
  return { ok: true };
}

export function editPlan() {
  // OMON-179 verify-loop fix: this OMON-035-era guard required developmental
  // goals, so "Create new draft" silently no-opped on a monitored-events-only
  // plan — the second goals-only guard in this family (confirmAndSaveLiveVersion
  // was the first, fixed in PR #421). Either stream non-empty is editable.
  if (!state.selectedPatient) return;
  if (state.patientGoals.length === 0 && state.patientMonitoredEvents.length === 0) return;

  // OMON-035: if a draft already exists for this EoC, resume it (regardless
  // of whether the user got here via Create new draft or by reopening the patient
  // after a refresh mid-edit). Drafts always win over re-cloning the active.
  if (state.patientDraft && state.patientDraft._draftState) {
    restoreDraftState(state.patientDraft._draftState);
    state.currentDraftId = state.patientDraft.docId;
    state.builderMode = "edit";
    renderClientsView();
    return;
  }

  // No existing draft — clone the live plan as the seed for a new draft.
  state.currentDraftId = null;

  // ☠️ Seed the office contact from the live plan's PINNED custodian. Without
  // this, opening a plan someone had deliberately routed and pressing save
  // would show "follows whoever saves" and then make it true — silently
  // moving responsibility, which is the exact failure FHIR split `author`
  // into `contributor[]` + `custodian` to prevent. An UNpinned custodian is
  // not seeded: it was only ever the last saver, and inheriting it as a pin
  // would turn a default into a decision nobody made.
  const livePlan = state.patientCarePlans[0];
  state.builderCustodian =
    livePlan?.custodianPinned && livePlan.custodianPersonId
      ? { personDocId: livePlan.custodianPersonId, name: null }
      : null;

  // Load plan duration from CarePlan.period if available
  const plan = state.patientCarePlans[0];
  if (plan) {
    const planData = plan as CarePlanInfo & { periodStart?: string; periodEnd?: string };
    if (planData.periodStart) {
      state.planStartDate = planData.periodStart;
      if (planData.periodEnd) {
        const start = new Date(planData.periodStart);
        const end = new Date(planData.periodEnd);
        state.planMonths = Math.max(3, (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth()));
      }
    }
  }

  // OMON-119 FULL: clone existing monitored events into the builder so
  // editing an existing plan preserves them. patientMonitoredEvents was
  // populated by loadPatientPlan (PR #1e40af). Without this clone, every
  // plan edit would silently drop the monitored events on save.
  state.builderMonitoredEvents = state.patientMonitoredEvents.map((m) =>
    // OMON-174/177/183 + ADR-0035 am.9a: carry every tuned value forward —
    // see builderFromPlanEntry (monitored.ts, unit-tested).
    builderFromPlanEntry(m, state.catalogueData.get(m.odCode)));
  // Ed 2026-09-29: a copy of an older plan still gets the minimum set.
  state.builderMonitoredEvents = ensureMinimumSet(state.builderMonitoredEvents, state.catalogueData);

  state.builderGoals = state.patientGoals.map((g) => {
    const name = g.measureCode || "";
    // Trajectory length tells us the goal's duration — use it if available
    const trajectoryLen = g._trajectory?.length || state.planMonths;
    // OMON-028: prefer stable code from the goal doc; fall back to resolving
    // via the paired ObservationDefinition for legacy goals that only carry
    // display strings in category[].
    const od = state.catalogueData.get(name);
    const h = od ? resolveODHierarchy(od) : null;
    return {
      name,
      title: g.description,
      goalCategory: g.category || h?.categoryDisplay || "",
      // Only carry a subcategory display when a real subcategory code exists —
      // otherwise resolveODHierarchy returns the "(unknown Goal Sub-category)"
      // placeholder, which on re-save would be written as the code (ADR-0022).
      subcategory: (g.subcategoryCode || h?.subcategoryCode)
        ? (g.subcategory || h?.subcategoryDisplay || "")
        : "",
      categoryCode: g.categoryCode || h?.categoryCode || "",
      storedCategoryCode: g.storedCategoryCode,
      subcategoryCode: g.subcategoryCode || h?.subcategoryCode || "",
      // OMON-275: typeof guards — `||` coerces 0, and while plan-side scores
      // stay 1–5, the idiom must not survive anywhere scores flow.
      currentScore: typeof g.currentScore === "number" ? g.currentScore : 0,
      targetScore: typeof g.targetScore === "number" ? g.targetScore : 3,
      baselineMonths: g._baselineMonths || 2,
      smartGoal: g._smartGoal || getSmartGoalFromActivity(name),
      mandatory: g.mandatory === true,
      shiftRoles: g.shiftRoles,
      // OMON-135 + 2026-05-24 follow-up: prefer per-plan carer-guidance
      // override; fall back to canonical when the override is absent OR
      // is the all-empty bug shape (planted by a race against
      // state.activityData not yet being loaded at snapshot time).
      // `chooseCarerGuidanceForLoad` encodes the policy + protects
      // against shape drift (wrong length / non-string elements).
      carerGuidance: chooseCarerGuidanceForLoad(
        g._carerGuidance,
        getCarerGuidanceFromActivity(name),
      ),
      guidanceOverride: planGuidanceOverrideOnLoad(
        g._carerGuidanceText,
        g._carerGuidance,
        getCarerGuidanceFromActivity(name),
        (state.activityData.get(name)?.documentation || []).map((d) => d.label || ""),
        composeFromLevels,
      ),
      trajectory: g._trajectory ? [...g._trajectory] : [],
      _overrides: new Set<number>(),
      goalStartMonth: 0,
      goalEndMonth: trajectoryLen,
    };
  });
  // Ensure trajectories are initialised, then derive the goal fields from the
  // (authoritative) cells so loaded goals match the OMON-187 cells-are-truth model.
  for (const obj of state.builderGoals) {
    if (obj.trajectory.length !== goalMonths(obj)) ensureTrajectory(obj);
    deriveGoalFields(obj);
  }
  state.builderMode = "edit";
  renderClientsView();
}


// ======================================================================
// Plan Builder — shared for create + edit
// ======================================================================

export function renderPlanBuilder() {
  if (!state.selectedPatient) return;
  const isEdit = state.builderMode === "edit";

  // OMON-110: render a single goal row in the flat list. `i` is the
  // objective's index in state.builderGoals. The row layout is:
  //   [drag-handle spine, full height] [content column]
  // where the content column preserves the original three-section vertical
  // stack (top-row + trajectory + SMART goal). SortableJS grabs by the
  // .drag-handle element.
  const renderGoalRow = (obj: SelectedGoal, i: number): string => {
    const categoryDisplay = state.goalCategories.get(obj.categoryCode)?.display
      || obj.goalCategory || "Uncategorised";
    return `
    <div class="card builder-row" data-builder-row-idx="${i}">
      <span class="drag-handle" title="Drag to reorder" aria-label="Drag to reorder">&#x283F;</span>
      <div class="builder-row-content">
      <div class="top-row">
        <div class="info">
          <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;">
            <span class="category-chip" style="background:#f2e4ee;color:#9d0a6d;padding:2px 8px;border-radius:10px;font-size:13px;font-weight:500;">${escapeHtml(categoryDisplay)}</span>
            <div class="title">${escapeHtml(obj.title)}</div>
          </div>
          <div class="meta">${escapeHtml(obj.subcategory)}</div>
        </div>
        <button class="remove-btn" data-remove-idx="${i}" title="Remove">&times;</button>
      </div>
      <div class="goal-trajectory-block">
        <div class="traj-head">
          <label>${goalLen(obj)}-month plan &middot; within ${state.planMonths}-month plan window</label>
        </div>
        <div class="builder-now-line" hidden></div>
        <div class="traj-chart-wrap"><canvas class="traj-chart" data-builder-goal="${escapeHtml(obj.name)}"></canvas></div>
        <div class="month-cells">
          ${renderMonthCells(obj, i)}
          <span class="period-control" data-period-edge>
            <button class="period-minus" data-period-idx="${i}" ${goalLen(obj) <= 2 ? "disabled" : ""} title="Remove last month" aria-label="Shorten">&minus;</button>
            <button class="period-plus" data-period-idx="${i}" ${goalLen(obj) >= 36 ? "disabled" : ""} title="Add a month" aria-label="Lengthen">&plus;</button>
          </span>
        </div>
        <div class="traj-legend">Assess = assessment month &middot; 1&ndash;5 = support level (1 fully supported &rarr; 5 independent)</div>
      </div>
      <div class="smart-goal-area">
        <label>What does good look like</label>
        <textarea class="smart-goal-textarea" rows="2" data-smart-idx="${i}"
          placeholder="Enter what good looks like for this goal..."
        >${escapeHtml(obj.smartGoal || "")}</textarea>
      </div>
      ${renderGoalPolicyLine(obj, i)}
      ${renderCarerGuidanceArea(obj, i)}
      </div>
    </div>
  `;
  };

  let objHtml: string;
  if (state.builderGoals.length === 0) {
    objHtml = `<div class="empty-state" style="padding:24px;">
      <h3>No goals selected</h3>
      <p>Add goals from the catalogue to build this SOMA plan.</p>
    </div>`;
  } else {
    // OMON-108: flat list. Goals render in state.builderGoals order; SortableJS
    // wires the drag handles up after render (see event-binding block below).
    // Category is communicated via the per-row chip in renderGoalRow; the
    // previous category-header grouping + per-group fold was removed because
    // (a) iOS reads CarePlan.goal[] as authoritative flat order already and
    // (b) reordering across categories was the planner's blocking gap.
    const rows = state.builderGoals.map((obj, i) => renderGoalRow(obj, i)).join("");
    objHtml = `<div id="builderGoalList">${rows}</div>`;
  }

  // OMON-035: editor entered always renders in draft mode (Option C — amber
  // tinted region, persistent banner, autosave indicator, Save Live Version
  // button). Both new-plan and revision cases — drafts are first-class.
  // Compute the version label: revisions of an existing v1 are drafts of v2;
  // a brand-new plan is a draft of v1.
  const draftVersion = state.patientCarePlans.length > 0
    ? (state.patientCarePlans[0]._version || 1) + 1
    : 1;
  const draftVersionLabel = `v${draftVersion} · draft`;
  const nowStamp = new Date().toLocaleTimeString("en-GB", { hour12: false });

  clientsView.innerHTML = `
    <div class="plan-header">
      <div>
        <h2 style="font-size:17px;margin-bottom:4px;">${isEdit ? "Edit" : "New"} Care Plan -- ${escapeHtml(state.selectedPatient.name)}</h2>
        <p style="font-size:13px;color:#6b7280;">Select goals and set predicted current / target scores</p>
      </div>
      <div style="display:flex;gap:8px;align-items:center;">
        <!-- Ed, 2026-09-24: ONE chip. "v2 · draft" already says draft; a
             second "Draft" badge said it twice. The button carried the
             item counts as "(0 + 1 ME)"; the panel below says them in words. -->
        <span class="badge badge-version">${escapeHtml(draftVersionLabel)}</span>
        <button class="back-btn" id="cancelBuilder">Discard</button>
        <button class="btn-amber" id="savePlanBtn" ${canPublish(isEmptyDraft(), hasLiveVersion()) ? "" : "disabled"}>
          Save Live Version
        </button>
      </div>
    </div>
    <div class="draft-region">
      <div class="draft-banner">
        <span class="draft-banner-message"><strong>You're editing a draft of this plan.</strong> Carers see the live plan until you Save Live Version.</span>
        <span class="autosave" id="__autosaveIndicator">Saved at ${nowStamp}</span>
      </div>
    <div class="plan-settings-bar">
      <label class="plan-setting">Start <input type="date" id="planStartInput" value="${state.planStartDate}"></label>
      <label class="plan-setting">Duration
        <button class="dur-btn" id="durMinus" ${state.planMonths <= 3 ? "disabled" : ""}>&#8722;</button>
        <span class="dur-display" id="durDisplay">${state.planMonths} mo</span>
        <button class="dur-btn" id="durPlus" ${state.planMonths >= 36 ? "disabled" : ""}>+</button>
      </label>
      ${renderOfficeContactSetting()}
    </div>
    ${renderBuilderTabStrip()}
    ${renderBuilderTabContent(objHtml)}
    </div>
  `;

  // Bind events
  // OMON-035: "Discard" deletes the draft Firestore doc (if any) and returns
  // to the patient detail view. Confirmation modal because deletion is
  // irreversible — the spec calls out that discarded drafts have no history.
  clientsView.querySelector("#cancelBuilder")?.addEventListener("click", () => {
    confirmAndDiscardDraft();
  });

  clientsView.querySelector("#addFromCatalogue")?.addEventListener("click", () => {
    state.builderMode = "pick";
    renderClientsView();
  });

  // Office contact for escalations (FHIR R5 `custodian`). The choice lives in
  // builder state and is written by the save block below — never here, so
  // abandoning the draft abandons the choice with it.
  clientsView.querySelector("#officeContactBtn")?.addEventListener("click", () => {
    void pickOfficeContact(state.builderCustodian?.personDocId ?? null).then((choice) => {
      if (!choice) return; // dismissed — leave the plan exactly as it was
      state.builderCustodian =
        choice.kind === "pick"
          ? { personDocId: choice.personDocId, name: choice.name }
          : null;
      void saveDraft(); // the choice is draft state; don't lose it on refresh
      renderClientsView();
    });
  });

  // A seeded custodian arrives as an id with no name (editPlan reads it from
  // the plan document, which stores a reference, not a label). Resolve it
  // once and re-render only if a name actually came back — a revoked office
  // role legitimately has none, and re-rendering on null would spin.
  const seeded = state.builderCustodian;
  if (seeded && !seeded.name && !nameLookupsAttempted.has(seeded.personDocId)) {
    nameLookupsAttempted.add(seeded.personDocId);
    void contactName(seeded.personDocId).then((name) => {
      if (!name) return;
      if (state.builderCustodian?.personDocId !== seeded.personDocId) return;
      state.builderCustodian = { personDocId: seeded.personDocId, name };
      renderClientsView();
    });
  }

  // OMON-183: goal-row Mandatory toggle (mirrors the ME policy toggles).
  clientsView.querySelectorAll<HTMLElement>("[data-goal-pol]").forEach((tog) => {
    tog.addEventListener("click", () => {
      const g = state.builderGoals[parseInt(tog.dataset.goalPolIdx!)];
      if (!g) return;
      // am.8: production's single switch = every role or none.
      setMandatoryForAll(g, !(itemRoles(g, defOf(g.name)).length > 0), defOf(g.name));
      renderClientsView();
    });
  });

  // OMON-183 P4: Author | Shift preview lens toggle.
  clientsView.querySelectorAll<HTMLElement>("[data-builder-lens]").forEach((el) => {
    el.addEventListener("click", () => {
      state.builderLens = el.dataset.builderLens as "author" | "preview";
      renderClientsView();
    });
  });

  // OMON-119 FULL: × remove button on each pending monitored event.
  clientsView.querySelectorAll<HTMLElement>("[data-remove-monitored]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const odCode = btn.dataset.removeMonitored!;
      // The minimum set is required on every plan — no × is rendered for it,
      // and a stale button can't remove it either.
      if (requiredMonitoredOdCodes(state.catalogueData).has(odCode)) return;
      state.builderMonitoredEvents = state.builderMonitoredEvents.filter(
        (m) => m.odCode !== odCode,
      );
      renderClientsView();
    });
  });

  clientsView.querySelector("#savePlanBtn")?.addEventListener("click", () => confirmAndSaveLiveVersion());

  // Plan settings: start date and duration +/-
  clientsView.querySelector("#planStartInput")?.addEventListener("change", (e) => {
    state.planStartDate = (e.target as HTMLInputElement).value;
    renderClientsView();
  });
  clientsView.querySelector("#durMinus")?.addEventListener("click", () => {
    if (state.planMonths > 3) {
      state.planMonths--;
      // OMON-187: truncate any goal longer than the new plan window — the kept
      // cells are preserved verbatim (no re-interpolation).
      for (const obj of state.builderGoals) {
        if (goalLen(obj) > state.planMonths) {
          obj.trajectory = obj.trajectory.slice(0, state.planMonths);
          deriveGoalFields(obj);
        }
      }
      renderClientsView();
    }
  });
  clientsView.querySelector("#durPlus")?.addEventListener("click", () => {
    if (state.planMonths < 36) {
      state.planMonths++;
      // Goals keep their current length; the planner extends each via its own +.
      renderClientsView();
    }
  });

  // OMON-187: month cells are the single source of truth. Setting a cell writes
  // the trajectory directly and re-derives baseline / target / assessment-period
  // (no dropdowns, no interpolation).
  clientsView.querySelectorAll<HTMLSelectElement>(".month-select").forEach((sel) => {
    sel.addEventListener("change", () => {
      const objIdx = parseInt(sel.dataset.objIdx!);
      const monthIdx = parseInt(sel.dataset.monthIdx!);
      const v = parseInt(sel.value);
      const obj = state.builderGoals[objIdx];
      ensureTrajectory(obj);
      obj.trajectory[monthIdx] = v;
      deriveGoalFields(obj);
      // OMON-188: targeted update — toggle the cell's assessment/scored class
      // and rebuild ONLY this goal's chart, no full re-render (keeps editing
      // smooth; a full renderClientsView would rebuild all charts per keystroke).
      const cell = sel.closest(".month-cell");
      if (cell) { cell.classList.toggle("assessment", v === 0); cell.classList.toggle("scored", v !== 0); }
      buildBuilderChartFor(obj);
    });
  });

  // OMON-187: per-goal period +/- — grow/shrink the plan length one month at a
  // time from the end. A new trailing month defaults to the previous month's
  // value (the line extends flat until edited). Lower bound 2 months; extending
  // a goal past the plan-level duration auto-grows that outer duration to match
  // (Ed, 2026-06-16), capped at 36.
  clientsView.querySelectorAll<HTMLButtonElement>(".period-plus").forEach((btn) => {
    btn.addEventListener("click", () => {
      const obj = state.builderGoals[parseInt(btn.dataset.periodIdx!)];
      ensureTrajectory(obj);
      if (goalLen(obj) >= 36) return;
      obj.trajectory.push(obj.trajectory[obj.trajectory.length - 1] ?? 0);
      deriveGoalFields(obj);
      if (goalLen(obj) > state.planMonths) state.planMonths = goalLen(obj);
      renderClientsView();
    });
  });
  clientsView.querySelectorAll<HTMLButtonElement>(".period-minus").forEach((btn) => {
    btn.addEventListener("click", () => {
      const obj = state.builderGoals[parseInt(btn.dataset.periodIdx!)];
      ensureTrajectory(obj);
      if (goalLen(obj) <= 2) return;
      obj.trajectory.pop();
      deriveGoalFields(obj);
      renderClientsView();
    });
  });

  // Remove buttons
  clientsView.querySelectorAll<HTMLButtonElement>("[data-remove-idx]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const idx = parseInt(btn.dataset.removeIdx!);
      state.builderGoals.splice(idx, 1);
      renderClientsView();
    });
  });

  // SMART goal textareas
  clientsView.querySelectorAll<HTMLTextAreaElement>("[data-smart-idx]").forEach((ta) => {
    ta.addEventListener("change", () => {
      const idx = parseInt(ta.dataset.smartIdx!);
      state.builderGoals[idx].smartGoal = ta.value;
    });
  });

  // OMON-174: monitored-event prompt textareas (same change-on-blur pattern
  // as SMART goal — no re-render, the value only matters at save time).
  clientsView.querySelectorAll<HTMLTextAreaElement>("[data-me-prompt-idx]").forEach((ta) => {
    ta.addEventListener("change", () => {
      const idx = parseInt(ta.dataset.mePromptIdx!);
      if (state.builderMonitoredEvents[idx]) state.builderMonitoredEvents[idx].prompt = ta.value;
    });
  });
  // OMON-232: per-part prompts (same change-on-blur pattern).
  clientsView.querySelectorAll<HTMLTextAreaElement>("[data-me-part-idx]").forEach((ta) => {
    ta.addEventListener("change", () => {
      const m = state.builderMonitoredEvents[parseInt(ta.dataset.mePartIdx!)];
      const code = ta.dataset.mePartCode;
      if (!m || !code) return;
      m.componentPrompts = { ...(m.componentPrompts ?? {}), [code]: ta.value };
    });
  });

  // OMON-179/177: per-shift policy toggles. Flip + re-render (the switch and
  // its number input's disabled state are visual). Number inputs persist on
  // change with no re-render (no other element depends on the value).
  clientsView.querySelectorAll<HTMLElement>("[data-me-pol]").forEach((tog) => {
    tog.addEventListener("click", () => {
      const idx = parseInt(tog.dataset.mePolIdx!);
      const m = state.builderMonitoredEvents[idx];
      const kind = tog.dataset.mePol as "mandatory" | "escalate" | "targetOn" | undefined;
      if (!m || !kind) return;
      if (kind === "mandatory") {
        // am.8: production's single switch = every role or none.
        const d = defOf(m.odCode);
        setMandatoryForAll(m, !(itemRoles(m, d).length > 0), d);
        renderClientsView();
        return;
      }
      m[kind] = m[kind] !== true;
      // OMON-183 P2: removing the inline target clears its value too.
      if (kind === "targetOn" && m.targetOn !== true) m.targetValue = null;
      // Turning Escalate on with no threshold yet seeds the librarian default.
      if (kind === "escalate" && m.escalate && (m.escalationThreshold === null || m.escalationThreshold === undefined)) {
        const od = state.catalogueData.get(m.odCode);
        m.escalationThreshold = typeof od?._escalationThresholdDefault === "number"
        ? od._escalationThresholdDefault
        : DEFAULT_ESCALATION_THRESHOLD;
      }
      renderClientsView();
    });
  });
  // OMON-358: the shift preview's "Preview as" role.
  clientsView.querySelectorAll<HTMLButtonElement>("[data-preview-role]").forEach((b) => {
    b.addEventListener("click", () => {
      previewRole = b.dataset.previewRole as ShiftRoleCode;
      renderClientsView();
    });
  });
  // ADR-0035 am.8: role ticks on goals AND monitored events, one handler.
  clientsView.querySelectorAll<HTMLInputElement>("[data-role-kind]").forEach((cb) => {
    cb.addEventListener("change", () => {
      const idx = parseInt(cb.dataset.roleIdx!);
      const isGoal = cb.dataset.roleKind === "goal";
      const item = isGoal ? state.builderGoals[idx] : state.builderMonitoredEvents[idx];
      if (!item) return;
      const code = isGoal ? (item as SelectedGoal).name : (item as BuilderMonitoredEvent).odCode;
      toggleRole(item, cb.dataset.role as ShiftRoleCode, defOf(code));
      renderClientsView();
    });
  });
  clientsView.querySelectorAll<HTMLInputElement>("[data-me-pol-num]").forEach((inp) => {
    inp.addEventListener("change", () => {
      const idx = parseInt(inp.dataset.mePolIdx!);
      const m = state.builderMonitoredEvents[idx];
      const field = inp.dataset.mePolNum as "escalationThreshold" | "targetValue" | undefined;
      if (!m || !field) return;
      const raw = inp.value.trim();
      m[field] = raw === "" ? null : Number(raw);
    });
  });
  // ADR-0035 am.9a: "Carers may mark this not applicable".
  clientsView.querySelectorAll<HTMLInputElement>("[data-me-allow-na-idx]").forEach((cb) => {
    cb.addEventListener("change", () => {
      const m = state.builderMonitoredEvents[parseInt(cb.dataset.meAllowNaIdx!)];
      if (!m) return;
      m.allowNotApplicable = cb.checked;
      renderClientsView();
    });
  });
  // above | below for the escalation threshold (Ed, 2026-09-02).
  clientsView.querySelectorAll<HTMLSelectElement>("[data-me-pol-dir]").forEach((sel) => {
    sel.addEventListener("change", () => {
      const idx = parseInt(sel.dataset.mePolIdx!);
      const m = state.builderMonitoredEvents[idx];
      if (!m) return;
      // Anything that is not an explicit "below" means "above" — the same
      // normalisation the save path and every reader applies, so a stray
      // value can never mean a third thing.
      m.escalationDirection = sel.value === "below" ? "below" : "above";
    });
  });

  // OMON-135 carer-guidance: expand/fold toggle per goal row.
  clientsView.querySelectorAll<HTMLButtonElement>("[data-carer-toggle-idx]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const idx = parseInt(btn.dataset.carerToggleIdx!);
      if (state.builderCarerGuidanceExpanded.has(idx)) {
        state.builderCarerGuidanceExpanded.delete(idx);
      } else {
        state.builderCarerGuidanceExpanded.add(idx);
      }
      renderClientsView();
    });
  });

  // Ed 2026-09-29: "Write guidance for this person" — start this plan's own
  // text from the catalogue's, then edit it in the one-box editor.
  clientsView.querySelectorAll<HTMLButtonElement>("[data-carer-own-idx]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const obj = state.builderGoals[parseInt(btn.dataset.carerOwnIdx!)];
      if (!obj) return;
      obj.guidanceOverride = catalogueGuidanceText(obj.name);
      renderClientsView();
    });
  });

  // One-box editor per open row: typing updates state (no re-render — that
  // would steal focus); the global autosave in main.ts debounces off `input`.
  state.builderGoals.forEach((obj, i) => {
    wireGuidanceEditor(clientsView, `planGuidance-${i}`, (v) => { obj.guidanceOverride = v; });
  });

  // "Use the catalogue guidance": drop this plan's text AND any legacy
  // per-level override, so the goal late-binds to the catalogue again.
  clientsView.querySelectorAll<HTMLButtonElement>("[data-carer-reset-all-idx]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const idx = parseInt(btn.dataset.carerResetAllIdx!);
      const obj = state.builderGoals[idx];
      if (!obj) return;
      obj.guidanceOverride = null;
      obj.carerGuidance = getCarerGuidanceFromActivity(obj.name);
      renderClientsView();
    });
  });

  // OMON-108: SortableJS drag-and-drop reorder across the whole flat list.
  // Drag handle is the .drag-handle span at the start of each row, so dragging
  // doesn't fire when the planner clicks a <select> or textarea inside the row.
  // onEnd reorders state.builderGoals in place and re-renders so React-style
  // index/data-idx bindings stay consistent.
  const goalListEl = clientsView.querySelector<HTMLElement>("#builderGoalList");
  if (goalListEl) {
    Sortable.create(goalListEl, {
      handle: ".drag-handle",
      animation: 150,
      ghostClass: "sortable-ghost",
      onEnd: (evt) => {
        const from = evt.oldIndex;
        const to = evt.newIndex;
        if (from === undefined || to === undefined || from === to) return;
        const [moved] = state.builderGoals.splice(from, 1);
        state.builderGoals.splice(to, 0, moved);
        renderClientsView();
      },
    });
  }

  const monitoredListEl = clientsView.querySelector<HTMLElement>("#builderMonitoredEventList");
  if (monitoredListEl) {
    Sortable.create(monitoredListEl, {
      handle: ".drag-handle",
      animation: 150,
      ghostClass: "sortable-ghost",
      onEnd: (evt) => {
        const from = evt.oldIndex;
        const to = evt.newIndex;
        if (from === undefined || to === undefined || from === to) return;
        const [moved] = state.builderMonitoredEvents.splice(from, 1);
        state.builderMonitoredEvents.splice(to, 0, moved);
        renderClientsView();
      },
    });
  }

  // OMON-188: create the per-goal trajectory charts now the canvases are in the
  // DOM (planned cells + actual readings, same chart as the read view).
  createBuilderCharts();
}

// ======================================================================
// Trajectory calculation
// ======================================================================

/** Compute an ISO date string for a month offset from plan start. */
/** OMON-232: the `_componentPrompts` field for a saved entry ({} when the fact has no parts). */
function partPromptsField(m: BuilderMonitoredEvent): Record<string, unknown> {
  const parts = partsOf(state.catalogueData.get(m.odCode));
  if (!parts.length) return {};
  const prompts = componentPromptsForSave(parts, m.componentPrompts);
  return Object.keys(prompts).length ? { _componentPrompts: prompts } : {};
}

function monthOffsetToDate(monthOffset: number): string {
  const start = new Date(state.planStartDate);
  const d = new Date(start.getFullYear(), start.getMonth() + monthOffset, 1);
  return d.toISOString().slice(0, 10);
}

function goalStartDateStr(obj: SelectedGoal): string {
  return monthOffsetToDate(obj.goalStartMonth);
}

function goalEndDateStr(obj: SelectedGoal): string {
  return monthOffsetToDate(obj.goalEndMonth);
}

/**
 * OMON-187: derive the persisted goal fields from the editable trajectory
 * cells. The cells are now the single source of truth (no dropdowns, no
 * interpolation): currentScore = the first cell (the value at plan start, 0
 * during assessment), baselineMonths = count of leading zeros (the assessment
 * period), targetScore = the final cell, goalEndMonth = start + length. The
 * on-disk shape is unchanged, so the read/plan-detail view and iOS are
 * unaffected — only the input method changed.
 */
function deriveGoalFields(obj: SelectedGoal) {
  const t = obj.trajectory || [];
  const len = t.length;
  let leadingZeros = 0;
  for (let i = 0; i < len; i++) {
    if ((t[i] || 0) > 0) break;
    leadingZeros++;
  }
  obj.currentScore = t[0] || 0;
  obj.baselineMonths = leadingZeros;
  obj.targetScore = t[len - 1] || 0;
  obj.goalEndMonth = obj.goalStartMonth + len;
}

/** Goal's current month count (the number of editable cells). */
function goalLen(obj: SelectedGoal): number {
  return obj.trajectory?.length ?? goalMonths(obj);
}

// OMON-188: per-goal trajectory Chart.js instances in the builder, keyed by goal
// name. Reuses the read view's buildTrajectoryChart so the builder shows the SAME
// chart (planned dashed line + actual readings + time axis + zoom/tooltip).
const builderCharts = new Map<string, Chart>();

/** Destroy a goal's builder chart (if any). */
function destroyBuilderChart(name: string) {
  builderCharts.get(name)?.destroy();
  builderCharts.delete(name);
}

/** (Re)build the Chart for one builder goal row from its live cells + the
 *  patient's actuals. Used both on full render and on in-place cell edits. */
function buildBuilderChartFor(obj: SelectedGoal) {
  const canvas = clientsView.querySelector<HTMLCanvasElement>(
    `canvas[data-builder-goal="${cssEscape(obj.name)}"]`,
  );
  if (!canvas) return;
  destroyBuilderChart(obj.name);
  const chart = buildTrajectoryChart(
    canvas,
    obj.trajectory || [],
    state.patientActuals[obj.name] || [],
    obj.baselineMonths || 0,
  );
  builderCharts.set(obj.name, chart);
  // OMON-191: pin this goal's editable cells under the chart's month columns.
  layoutBuilderRow(obj);
}

/**
 * OMON-191: position one goal's editable cell strip so each cell sits directly
 * under its month on the full-plan timeline chart above. The chart's x scale is
 * pinned planStart..planEnd, so `getPixelForValue(addMonths(planStart, i))` is
 * the exact left edge of month i in the same coordinate space as the cell strip
 * (the canvas and the .month-cells row share the block's left origin + width).
 *
 * Layout: pad the grid left by the goal's start offset, give each of the goal's
 * months an explicit pixel column, and leave a trailing `auto` column as the
 * blank track. The +/- control floats at the goal's right edge; a "now" line and
 * past-month dimming make it clear which edits are behind/ahead of today.
 */
function layoutBuilderRow(obj: SelectedGoal) {
  const chart = builderCharts.get(obj.name);
  const canvas = clientsView.querySelector<HTMLCanvasElement>(
    `canvas[data-builder-goal="${cssEscape(obj.name)}"]`,
  );
  if (!chart || !canvas) return;
  const block = canvas.closest<HTMLElement>(".goal-trajectory-block");
  const cells = block?.querySelector<HTMLElement>(".month-cells");
  if (!block || !cells) return;
  const xScale = chart.scales.x;
  if (!xScale) return;

  const planStart = new Date(state.planStartDate);
  const px = (monthOffset: number) => xScale.getPixelForValue(addMonths(planStart, monthOffset).getTime());
  const start = obj.goalStartMonth || 0;
  const len = obj.trajectory?.length || 0;
  if (len === 0) return;

  // Explicit pixel column per goal month + a trailing blank-track column.
  const widths: string[] = [];
  for (let m = start; m < start + len; m++) widths.push(`${Math.max(2, px(m + 1) - px(m))}px`);
  cells.style.paddingLeft = `${Math.max(0, px(start))}px`;
  cells.style.gridTemplateColumns = `${widths.join(" ")} auto`;

  // Dim months whose whole span is already in the past (end <= now).
  const now = Date.now();
  cells.querySelectorAll<HTMLElement>(".month-cell").forEach((el, idx) => {
    el.classList.toggle("past", addMonths(planStart, start + idx + 1).getTime() <= now);
  });

  // Float the +/- control at the goal's right edge, vertically on the select row,
  // clamped so a full-length goal's control stays inside the strip.
  const ctrl = cells.querySelector<HTMLElement>(".period-control[data-period-edge]");
  if (ctrl) {
    const selTop = cells.querySelector<HTMLElement>(".month-cell .month-select")?.offsetTop ?? 0;
    const rightEdge = px(start + len);
    const maxLeft = cells.clientWidth - ctrl.offsetWidth - 2;
    ctrl.style.top = `${selTop}px`;
    ctrl.style.left = `${Math.max(0, Math.min(rightEdge + 4, maxLeft))}px`;
  }

  // "Now" line spanning the chart + cells (hidden if today is outside the plan).
  const nowLine = block.querySelector<HTMLElement>(".builder-now-line");
  const wrap = block.querySelector<HTMLElement>(".traj-chart-wrap");
  if (nowLine && wrap) {
    const nowPx = xScale.getPixelForValue(now);
    if (nowPx >= px(0) && nowPx <= px(state.planMonths)) {
      const top = wrap.offsetTop;
      nowLine.hidden = false;
      nowLine.style.left = `${nowPx}px`;
      nowLine.style.top = `${top}px`;
      nowLine.style.height = `${cells.offsetTop + cells.offsetHeight - top}px`;
    } else {
      nowLine.hidden = true;
    }
  }
}

/** Create all builder charts after a full render; destroys any stale instances. */
function createBuilderCharts() {
  for (const c of builderCharts.values()) c.destroy();
  builderCharts.clear();
  for (const obj of state.builderGoals) buildBuilderChartFor(obj);
  ensureBuilderResizeObserver();
}

// OMON-191: one ResizeObserver re-pins every goal's cell strip when the charts
// reflow (window resize, sidebar toggle). Created once; watches the goal list.
let builderResizeObserver: ResizeObserver | null = null;
let builderResizeRaf = 0;
function ensureBuilderResizeObserver() {
  const list = clientsView.querySelector<HTMLElement>("#builderGoalList");
  if (!list || builderResizeObserver) return;
  builderResizeObserver = new ResizeObserver(() => {
    cancelAnimationFrame(builderResizeRaf);
    // Defer one frame so the charts' own resize settles before we read pixels.
    builderResizeRaf = requestAnimationFrame(() => {
      if (!clientsView.querySelector("#builderGoalList")) {
        builderResizeObserver?.disconnect();
        builderResizeObserver = null;
        return;
      }
      for (const obj of state.builderGoals) layoutBuilderRow(obj);
    });
  });
  builderResizeObserver.observe(list);
}

/** Minimal CSS.escape fallback for the goal-name attribute selector. */
function cssEscape(s: string): string {
  return (window.CSS && CSS.escape) ? CSS.escape(s) : s.replace(/["\\]/g, "\\$&");
}

/**
 * OMON-187: the unified editable cell row. Every cell is a 0-5 picker (0 =
 * assessment-period month, 1-5 = support level). No disabled anchors and no
 * interpolation — the planner sets each month directly and the derived
 * baseline/target/period fall out via deriveGoalFields.
 */
function renderMonthCells(obj: SelectedGoal, objIdx: number): string {
  const t = ensureTrajectory(obj);
  const labels = getMonthLabelsFor(obj.goalStartMonth, t.length);
  let cells = "";
  for (let m = 0; m < t.length; m++) {
    const val = t[m] || 0;
    const cls = val === 0 ? "assessment" : "scored";
    let opts = "";
    for (let s = 0; s <= 5; s++) {
      // OMON-275: the stored 0 is the ASSESSMENT-MONTH SENTINEL (no expected
      // reading), NOT a score — recorded scores of 0 live only in Observations.
      // Label it "Assess" so the only visible numeric 0 in the system is the
      // carer's "attempted, unable to start".
      const label = s === 0 ? "Assess" : String(s);
      opts += `<option value="${s}" ${s === val ? "selected" : ""}>${label}</option>`;
    }
    cells += `
      <div class="month-cell ${cls}">
        <span class="month-label">${labels[m]}</span>
        <select class="month-select" data-obj-idx="${objIdx}" data-month-idx="${m}">
          ${opts}
        </select>
      </div>`;
  }
  return cells;
}

function getSmartGoalFromActivity(name: string): string {
  const od = state.catalogueData.get(name);
  if (od?._smartGoalTemplate) return od._smartGoalTemplate;
  return "";
}

/**
 * OMON-135: snapshot the canonical per-level carer guidance from
 * `_activityDefinition.documentation[]` into a 5-string array. Used at
 * goal-add time and as the fallback when the per-plan override is
 * absent. Missing levels become empty strings — readers can decide
 * whether to render empty or fall back further.
 */
function getCarerGuidanceFromActivity(name: string): string[] {
  const ad = state.activityData.get(name);
  const docs = ad?.documentation || [];
  return [0, 1, 2, 3, 4].map((i) => docs[i]?.display || "");
}

/**
 * OMON-135 + 2026-05-24 follow-up: predicate — does the builder row's
 * carer guidance differ from the current canonical
 * (`_activityDefinition.documentation[]`) in a way that's worth
 * persisting?
 *
 * Used at save time to decide whether to persist `_carerGuidance` as
 * a snapshot override or omit the field and let readers late-bind.
 *
 * Refuses to flag "all-empty in-memory + non-empty canonical" as a
 * real override, because that shape is the stale-state bug planted
 * when state.activityData wasn't loaded at goal-add time. Delegates
 * to `isMeaningfulOverride` for the policy.
 */
function carerGuidanceIsOverride(obj: SelectedGoal): boolean {
  return isMeaningfulOverride(
    obj.carerGuidance,
    getCarerGuidanceFromActivity(obj.name),
  );
}

/** Ed 2026-09-29: the catalogue's guidance for a goal as one text (saved, else composed). */
function catalogueGuidanceText(name: string): string {
  return resolveGuidanceText(null, state.activityData.get(name) as unknown as Record<string, unknown>) ?? "";
}

/** The `_carerGuidanceText` field for a goal's save, or nothing (late-binds to the catalogue). */
function planGuidanceTextField(obj: SelectedGoal): { _carerGuidanceText?: string } {
  const t = planGuidanceTextToWrite(obj.guidanceOverride, catalogueGuidanceText(obj.name));
  return t ? { _carerGuidanceText: t } : {};
}

/**
 * OMON-135: render the per-row carer-guidance area. Folded by default
 * (single button); expanded shows 5 markdown editors with edit/preview
 * toggle per level and a "Reset all" affordance. Event-handler wiring
 * lives in `bindCarerGuidanceHandlers` below.
 */
/**
 * OMON-183: the goal row's Mandatory toggle — same defaults-not-gates rule
 * as the ME rows (librarian's OD default seeds it; amber chip names the
 * default when the planner deviates).
 */
function renderGoalPolicyLine(obj: SelectedGoal, i: number): string {
  return `<div style="padding:8px 2px;border-top:1px solid #f3f4f6;font-size:13px;">${
    renderRolesControl("goal", i, obj, defOf(obj.name), "scoring this goal")}</div>`;
}

/** The catalogue definition behind a builder item (goal name / event odCode). */
function defOf(code: string): Record<string, unknown> | undefined {
  return state.catalogueData.get(code) as unknown as Record<string, unknown> | undefined;
}

/**
 * ADR-0035 am.8: who owes this item, identical for goals and monitored
 * events. Off production: four role checkboxes (read-only when the librarian
 * has locked the item). Production: one Mandatory switch (every role / none),
 * because production carers can't choose a role yet.
 */
function renderRolesControl(
  kind: "goal" | "me",
  i: number,
  item: RoleBearing,
  def: Record<string, unknown> | undefined,
  doing: string,
): string {
  const locked = isLocked(def);
  const owed = itemRoles(item, def);
  const chip = locked
    ? `<span style="font-size:11px;color:#92400e;background:#fffbeb;border:1px solid #fde68a;border-radius:5px;padding:1px 6px;margin-left:6px;font-weight:700;">set by the librarian</span>`
    : differsFromDefault(item, def)
      ? `<span style="font-size:11px;color:#92400e;background:#fffbeb;border:1px solid #fde68a;border-radius:5px;padding:1px 6px;margin-left:6px;font-weight:700;">librarian default: ${escapeHtml(seedRoles(def).map(shiftRoleDisplay).join(", ") || "none")}</span>`
      : `<span style="font-size:11px;color:#6b7280;background:#f3f4f6;border-radius:5px;padding:1px 6px;margin-left:6px;">librarian default</span>`;
  if (!shiftRolesAvailable(appEnv)) {
    const on = owed.length > 0;
    const attr = kind === "goal"
      ? `data-goal-pol="mandatory" data-goal-pol-idx="${i}"`
      : `data-me-pol="mandatory" data-me-pol-idx="${i}"`;
    return `<div style="display:flex;align-items:center;gap:8px;"><span style="flex:1;"><b style="font-weight:600;">Mandatory</b> ${chip}<div style="font-size:11px;color:#6b7280;">Carer can’t complete the shift without ${doing}</div></span>
      <span ${locked ? "" : attr} role="switch" aria-checked="${on}" aria-disabled="${locked}" style="flex:0 0 auto;width:38px;height:21px;border-radius:11px;${locked ? "opacity:.6;" : "cursor:pointer;"}user-select:none;position:relative;background:${on ? "#16a34a" : "#d1d5db"};transition:background .15s;">
        <span style="position:absolute;top:2px;left:${on ? "19px" : "2px"};width:17px;height:17px;border-radius:50%;background:#fff;transition:left .15s;box-shadow:0 1px 2px rgba(0,0,0,.25);"></span>
      </span></div>`;
  }
  const boxes = SHIFT_ROLES.map((r) => `<label style="display:inline-flex;align-items:center;gap:4px;font-size:13px;color:#374151;${locked ? "opacity:.6;" : "cursor:pointer;"}"><input type="checkbox" data-role-kind="${kind}" data-role-idx="${i}" data-role="${r.code}" ${owed.includes(r.code) ? "checked" : ""} ${locked ? "disabled" : ""}> ${escapeHtml(r.display)}</label>`).join("");
  return `<span style="flex:1;"><b style="font-weight:600;">Owed by</b> ${chip}<div style="font-size:11px;color:#6b7280;">${escapeHtml(ownedByHelp(carersChooseRoles(appEnv)))}</div><div style="display:flex;gap:12px;flex-wrap:wrap;margin-top:6px;">${boxes}</div></span>`;
}

function renderCarerGuidanceArea(obj: SelectedGoal, i: number): string {
  const expanded = state.builderCarerGuidanceExpanded.has(i);
  const isOverride = typeof obj.guidanceOverride === "string";
  const chevron = expanded ? "▼" : "▶";
  const overrideBadge = isOverride
    ? `<span class="carer-guidance-override-badge" title="This plan has its own guidance for this person">this person</span>`
    : "";

  if (!expanded) {
    return `
      <div class="carer-guidance-area">
        <button class="carer-guidance-toggle" data-carer-toggle-idx="${i}" type="button">
          <span class="chevron">${chevron}</span>
          <span class="carer-guidance-toggle-label">Carer guidance</span>
          ${overrideBadge}
        </button>
      </div>
    `;
  }

  // Ed 2026-09-29: one text. Until the planner chooses to write this person's
  // own guidance, the row shows the catalogue's (read-only, as the carer sees it).
  const own = typeof obj.guidanceOverride === "string";
  const body = own
    ? guidanceEditorHtml(`planGuidance-${i}`, obj.guidanceOverride as string, true)
    : `<div class="gtx-phone"><div class="gtx-phone-cap">What the carer sees — the catalogue's guidance</div>${
      renderGuidanceHtml(parseGuidanceText(catalogueGuidanceText(obj.name)))}</div>`;
  const action = own
    ? `<button class="carer-guidance-reset-all" data-carer-reset-all-idx="${i}" type="button">Use the catalogue guidance</button>`
    : `<button class="carer-guidance-reset-all" data-carer-own-idx="${i}" type="button">Write guidance for this person</button>`;

  return `
    <div class="carer-guidance-area expanded">
      <div class="carer-guidance-header">
        <button class="carer-guidance-toggle expanded" data-carer-toggle-idx="${i}" type="button">
          <span class="chevron">${chevron}</span>
          <span class="carer-guidance-toggle-label">Carer guidance</span>
          ${overrideBadge}
        </button>
        ${action}
      </div>
      ${body}
    </div>
  `;
}

// ======================================================================
// Objective Picker
// ======================================================================

export function renderObjectivePicker() {
  if (!state.selectedPatient) return;
  // OMON-183 P3: the picker is the single add path for BOTH worlds.
  const alreadySelected = new Set([
    ...state.builderGoals.map((o) => o.name),
    ...state.builderMonitoredEvents.map((m) => m.odCode),
  ]);

  // Group catalogue by stable code; display resolves live from hierarchy map.
  type PickerAim = { subcategoryCode: string; subcategoryDisplay: string; subcategoryOrder: number; objectives: ObservationDefinition[] };
  type PickerCat = { catCode: string; catDisplay: string; catOrder: number; subcategories: Map<string, PickerAim> };
  const grouped = new Map<string, PickerCat>();
  for (const [, od] of state.catalogueData) {
    const h = resolveODHierarchy(od);
    const catCode = h.categoryCode || "uncategorised";
    if (!grouped.has(catCode)) {
      const catNode = state.goalCategories.get(catCode);
      grouped.set(catCode, {
        catCode,
        catDisplay: catNode?.display || h.categoryDisplay || "Uncategorised",
        catOrder: catNode?.order ?? 999,
        subcategories: new Map(),
      });
    }
    const catEntry = grouped.get(catCode)!;
    if (!catEntry.subcategories.has(h.subcategoryCode)) {
      const aimNode = h.subcategoryCode ? state.goalSubcategories.get(`${catCode}__${h.subcategoryCode}`) : undefined;
      catEntry.subcategories.set(h.subcategoryCode, {
        subcategoryCode: h.subcategoryCode,
        subcategoryDisplay: aimNode?.display || h.subcategoryDisplay,
        subcategoryOrder: aimNode?.order ?? 999,
        objectives: [],
      });
    }
    catEntry.subcategories.get(h.subcategoryCode)!.objectives.push(od);
  }

  const sortedCats = [...grouped.values()].sort(
    (a, b) => a.catOrder - b.catOrder || a.catDisplay.localeCompare(b.catDisplay),
  );

  // OMON-070: picker fold state. Categories and subcategories fold via header
  // click; chevron rotates per the existing CSS rules in index.html
  // (.category-header.collapsed .chevron, .category-body.collapsed).
  let html = "";
  for (const cat of sortedCats) {
    const catKey = `cat:${cat.catCode}`;
    const catCollapsed = state.pickerCollapsedSections.has(catKey);
    const catObjCount = [...cat.subcategories.values()].reduce((n, a) => n + a.objectives.length, 0);
    let catHtml = "";
    const sortedSubcategories = [...cat.subcategories.values()].sort(
      (a, b) => a.subcategoryOrder - b.subcategoryOrder || a.subcategoryDisplay.localeCompare(b.subcategoryDisplay),
    );
    for (const aim of sortedSubcategories) {
      const aimKey = `subcategory:${cat.catCode}__${aim.subcategoryCode}`;
      const aimCollapsed = state.pickerCollapsedSections.has(aimKey);
      let aimHtml = "";
      for (const od of aim.objectives) {
        const selected = alreadySelected.has(od.name);
        // OMON-183 P3: capability chips tell the planner what adding this
        // fact activates (D -> goal row, M -> monitored-event row, both -> both).
        const caps = trackableCapabilities(od);
        const chips = (caps.includes("developmental") ? `<span style="font-size:11px;font-weight:700;border-radius:4px;padding:0 4px;background:#f5f3ff;color:#7c3aed;margin-left:6px;">D</span>` : "")
          + (caps.includes("monitored") ? `<span style="font-size:11px;font-weight:700;border-radius:4px;padding:0 4px;background:#eff6ff;color:#1e40af;margin-left:3px;">M</span>` : "")
          + (caps.includes("safety") ? `<span style="font-size:11px;font-weight:700;border-radius:4px;padding:0 4px;background:#fef2f2;color:#991b1b;margin-left:3px;">S</span>` : "");
        aimHtml += `
          <div class="objective-row ${selected ? "objective-selected" : ""}" data-pick-name="${escapeHtml(od.name)}">
            <span class="objective-title">${selected ? "&#10003; " : ""}${escapeHtml(od.title)}${chips}</span>
            <span class="objective-scale">${selected ? "added" : "click to add"}</span>
          </div>`;
      }
      if (aim.subcategoryCode) {
        catHtml += `
          <div class="aim-label ${aimCollapsed ? "collapsed" : ""}" data-picker-section-key="${escapeHtml(aimKey)}" style="cursor:pointer;">
            <span class="chevron">&#9660;</span>${escapeHtml(aim.subcategoryDisplay)}<span class="obj-count">${aim.objectives.length}</span>
          </div>
          <div class="aim-body ${aimCollapsed ? "collapsed" : ""}">${aimHtml}</div>`;
      } else {
        // No aim code → no fold wrapper; render rows directly under category.
        catHtml += aimHtml;
      }
    }
    html += `
      <div class="category-header ${catCollapsed ? "collapsed" : ""}" data-picker-section-key="${escapeHtml(catKey)}" style="cursor:pointer;">
        <span class="chevron">&#9660;</span>${escapeHtml(cat.catDisplay)}<span class="obj-count">${catObjCount}</span>
      </div>
      <div class="category-body ${catCollapsed ? "collapsed" : ""}">${catHtml}</div>`;
  }

  clientsView.innerHTML = `
    <div class="plan-header">
      <div>
        <h2 style="font-size:17px;margin-bottom:4px;">Add from catalogue</h2>
        <p style="font-size:13px;color:#6b7280;">Click a fact to add it — D activates a developmental goal, M a monitored event, both activate both (${state.builderGoals.length + state.builderMonitoredEvents.length} selected)</p>
      </div>
      <button class="btn-primary" id="donePickingBtn">Done (${state.builderGoals.length + state.builderMonitoredEvents.length})</button>
    </div>
    <input type="text" class="search-bar" id="pickerSearch" placeholder="Search objectives...">
    <div id="pickerList">${html}</div>
  `;

  // Bind events
  clientsView.querySelector("#donePickingBtn")?.addEventListener("click", () => {
    state.builderMode = state.patientCarePlans.length > 0 && state.builderGoals.length > 0 ? "edit" : "create";
    // OMON-121 follow-up: closing the developmental picker lands on the
    // developmental tab so the planner immediately sees what they just added.
    state.detailActiveTab = "developmental";
    renderClientsView();
  });

  clientsView.querySelector("#pickerSearch")?.addEventListener("input", (e) => {
    const term = (e.target as HTMLInputElement).value.toLowerCase();
    // OMON-070: when search clears, re-render so the picker returns to
    // the user's saved fold state (search-active mode forces all bodies
    // visible; without this re-render, anything the user collapsed
    // before searching would stay visually expanded).
    if (!term) {
      renderObjectivePicker();
      // Re-focus the search input — it's a fresh DOM element after re-render.
      (clientsView.querySelector("#pickerSearch") as HTMLInputElement | null)?.focus();
      return;
    }
    // OMON-070: search active → force-show all category-body / aim-body
    // even if the user had folded them, so search hits aren't hidden by
    // an earlier collapse.
    clientsView.querySelectorAll<HTMLElement>(".category-body, .aim-body").forEach((body) => {
      body.classList.remove("collapsed");
    });
    clientsView.querySelectorAll(".objective-row").forEach((row) => {
      const title = row.querySelector(".objective-title")?.textContent?.toLowerCase() || "";
      (row as HTMLElement).style.display = title.includes(term) ? "" : "none";
    });
    // OMON-044 + OMON-070: hide headers whose body has no visible rows.
    // After OMON-070's fold restructure, aim-label and category-header
    // each have a sibling `.aim-body` / `.category-body` that contains
    // their objective-rows. Hide both header and body when no rows match
    // the current search term.
    clientsView.querySelectorAll<HTMLElement>(".aim-label").forEach((header) => {
      const body = header.nextElementSibling;
      const hasVisible = body?.classList.contains("aim-body")
        && [...body.querySelectorAll<HTMLElement>(".objective-row")]
            .some((r) => r.style.display !== "none");
      header.style.display = hasVisible ? "" : "none";
      if (body instanceof HTMLElement) body.style.display = hasVisible ? "" : "none";
    });
    clientsView.querySelectorAll<HTMLElement>(".category-header").forEach((header) => {
      const body = header.nextElementSibling;
      const hasVisible = body?.classList.contains("category-body")
        && [...body.querySelectorAll<HTMLElement>(".objective-row")]
            .some((r) => r.style.display !== "none");
      header.style.display = hasVisible ? "" : "none";
      if (body instanceof HTMLElement) body.style.display = hasVisible ? "" : "none";
    });
  });

  // OMON-070: fold-toggle on category and aim headers in the picker.
  // Re-renders the picker to apply the new fold state. Toggling does not
  // affect `state.collapsedSections` (catalogue tab).
  clientsView.querySelectorAll<HTMLElement>("[data-picker-section-key]").forEach((el) => {
    el.addEventListener("click", (e) => {
      // Don't fold-toggle when the click landed on an objective-row
      // (defence — though objective-rows are inside the body, not the
      // header, so this is mostly belt-and-braces).
      if ((e.target as HTMLElement).closest(".objective-row")) return;
      const key = el.dataset.pickerSectionKey!;
      if (state.pickerCollapsedSections.has(key)) state.pickerCollapsedSections.delete(key);
      else state.pickerCollapsedSections.add(key);
      renderObjectivePicker();
    });
  });

  clientsView.querySelectorAll<HTMLElement>(".objective-row[data-pick-name]").forEach((row) => {
    row.addEventListener("click", () => {
      const name = row.dataset.pickName!;
      // OMON-183 P3: route by capability — D toggles a goal entry, M toggles
      // a monitored-event entry, D+M toggles both together. Facts with
      // neither coding fall back to the goal path (legacy/uncoded data).
      const pickOd = state.catalogueData.get(name);
      const caps = trackableCapabilities(pickOd ?? ({ category: [] } as never));
      const wantsGoal = caps.includes("developmental") || !caps.includes("monitored");
      const wantsMe = caps.includes("monitored");
      const goalIdx = state.builderGoals.findIndex((o) => o.name === name);
      const meIdx = state.builderMonitoredEvents.findIndex((m) => m.odCode === name);
      const isSelected = goalIdx >= 0 || meIdx >= 0;
      if (isSelected) {
        if (goalIdx >= 0) state.builderGoals.splice(goalIdx, 1);
        if (meIdx >= 0) state.builderMonitoredEvents.splice(meIdx, 1);
        renderObjectivePicker();
        return;
      }
      if (wantsMe && pickOd) {
        const h = resolveODHierarchy(pickOd);
        // Only carry a subcategory display when the fact actually has a
        // subcategory code. For sub-less facts (e.g. ex-monitored-events now
        // homed directly under a functional category) resolveODHierarchy
        // returns the "(unknown Goal Sub-category)" placeholder — don't
        // persist that into the entry; leave subcategory undefined (matches
        // how other sub-less ME entries are stored). ADR-0022 unification.
        state.builderMonitoredEvents.push(
          seedBuilderMonitoredEvent(pickOd, h.subcategoryCode ? h.subcategoryDisplay : undefined),
        );
      }
      if (!wantsGoal) {
        renderObjectivePicker();
        return;
      }
      const idx = state.builderGoals.findIndex((o) => o.name === name);
      if (idx >= 0) {
        state.builderGoals.splice(idx, 1);
      } else {
        const od = state.catalogueData.get(name);
        if (od) {
          const h = resolveODHierarchy(od);
          const obj: SelectedGoal = {
            name: od.name,
            title: od.title,
            goalCategory: h.categoryDisplay,
            // Sub-less facts: store "" not the "(unknown Goal Sub-category)"
            // placeholder. Otherwise the live-save fallback
            // (`subcategoryCode = obj.subcategoryCode || obj.subcategory`)
            // would write that placeholder string as the _goal's subcategory
            // CODE — real data corruption, reachable for any sub-less
            // developmental fact post the 2026-06-08 flatten. ADR-0022.
            subcategory: h.subcategoryCode ? h.subcategoryDisplay : "",
            categoryCode: h.categoryCode,
            subcategoryCode: h.subcategoryCode,
            currentScore: 0,
            targetScore: 3,
            baselineMonths: 2,
            smartGoal: getSmartGoalFromActivity(od.name),
            // ADR-0035 am.8: seeded from the librarian's default role set.
            shiftRoles: seedRoles(od as unknown as Record<string, unknown>),
            mandatory: seedRoles(od as unknown as Record<string, unknown>).length > 0,
            // OMON-135 + 2026-05-24 follow-up: snapshot canonical guidance
            // into builder state — but only if canonical has at least
            // one non-empty level. When state.activityData hasn't fully
            // loaded yet (race at goal-add), canonical reads as five
            // empty strings; we leave `carerGuidance` as `[]` rather
            // than seed the bug shape. The edit handler re-snaps from
            // canonical on first keystroke (line 482), and the load
            // path recovers if the user saves + reloads.
            carerGuidance: chooseCarerGuidanceForSnapshot(
              getCarerGuidanceFromActivity(od.name),
            ) ?? [],
            trajectory: [],
            _overrides: new Set(),
            goalStartMonth: 0,
            goalEndMonth: state.planMonths,
          };
          ensureTrajectory(obj);
          deriveGoalFields(obj);
          state.builderGoals.push(obj);
        }
      }
      renderObjectivePicker();
    });
  });
}

// ======================================================================
// Save Care Plan + Goals to Firestore
// ======================================================================

function isEmptyDraft(): boolean {
  return isEmptyPlan(state.builderGoals.length, state.builderMonitoredEvents.length);
}

function hasLiveVersion(): boolean {
  return state.patientCarePlans.length > 0;
}

export async function savePlan() {
  if (!state.selectedPatient) return;
  // An empty version may replace a live one (publish-rules.ts).
  if (!canPublish(isEmptyDraft(), hasLiveVersion())) return;
  // Last line of defence: no published version without the minimum set,
  // whichever path built it (Ed 2026-09-29).
  state.builderMonitoredEvents = ensureMinimumSet(state.builderMonitoredEvents, state.catalogueData);

  const saveBtn = clientsView.querySelector("#savePlanBtn") as HTMLButtonElement;
  saveBtn.disabled = true;
  saveBtn.textContent = "Saving...";

  try {
    const batch = writeBatch(db);
    const eocRef = doc(db, "_episodeofcare", state.selectedPatient.docId);

    // Read the EoC doc once — used for both supersede traversal
    // (eoc.carePlan[]) and FHIR Reference resolution (subject/careTeam/encounter).
    const eocSnap = await getDoc(eocRef);
    const eocData = eocSnap.data();
    const careTeamRef = eocData?.careTeam?.[0]?.reference as DocumentReference | undefined;
    const encounterRef = eocData?.reason?.[0]?.reference as DocumentReference | undefined;

    // OMON-032 + OMON-035 + OMON-045: supersede every existing active
    // _carePlan for this EoC before writing the new one, in the same
    // writeBatch. The new doc carries `_supersedes` (forward pointer) per
    // CAREPLAN_LIFECYCLE_MODEL.md and FHIR R5's `CarePlan.replaces[]`
    // convention.
    //
    // Step B4 (data-model-drift audit 2026-05-21): traverse from
    // eoc.carePlan[] (Pattern A canonical) instead of querying
    // `_carePlan where _episodeOfCareId == ...` (Pattern B denorm). The
    // parent-side Reference array is the source of truth post-Step-B
    // (`architecture/audits/2026-05-21-omon-data-model-drift.md`).
    const carePlanRef = doc(collection(db, "_carePlan"));
    const carePlanEntries = (eocData?.carePlan || []) as Array<{ reference?: DocumentReference }>;
    const existingSnapshots = await Promise.all(
      carePlanEntries
        .map((entry) => entry?.reference)
        .filter((ref): ref is DocumentReference => ref instanceof DocumentReference)
        .map((ref) => getDoc(ref))
    );
    const supersededAt = new Date().toISOString();
    let maxPriorVersion = 0;
    let mostRecentPriorRef: DocumentReference | null = null;
    let mostRecentPriorCreated = "";
    // The live version's stewardship travels to the new version: FHIR
    // `contributor[]` accumulates, and a PINNED `custodian` survives an edit
    // by someone else (see ./plan-custodian).
    let mostRecentPriorData: Record<string, unknown> | null = null;
    existingSnapshots.forEach((d) => {
      if (!d.exists()) return; // tolerate missing targets (FHIR resilient-references)
      const data = d.data()!;
      // OMON-062: walk the WHOLE supersede chain (active + superseded) to
      // find max(_version) so newVersion increments monotonically across
      // every publish.
      const v = data._version || 1;
      if (v > maxPriorVersion) maxPriorVersion = v;
      if (data.status !== "active") return;
      const created = data.created || "";
      if (created > mostRecentPriorCreated) {
        mostRecentPriorCreated = created;
        mostRecentPriorRef = d.ref;
        mostRecentPriorData = data;
      }
      batch.update(d.ref, {
        status: "superseded",
        _supersededAt: supersededAt,
      });
    });
    // OMON-062: increment past the chain's max, not the current active count.
    const newVersion = maxPriorVersion + 1;

    const goalRefs: DocumentReference[] = [];

    // OMON-108: flat global order index across all goals in the plan.
    // Replaces the within-category numbering from OMON-028. Position in
    // state.builderGoals (which is the planner's drag-reordered sequence)
    // becomes the authoritative order on read.
    const orderByIndex: number[] = state.builderGoals.map((_, i) => i);

    for (let bi = 0; bi < state.builderGoals.length; bi++) {
      const obj = state.builderGoals[bi];
      const goalRef = doc(collection(db, "_goal"));
      goalRefs.push(goalRef);

      // Prefer stable hierarchy codes. ☠️ Never the "(unknown Goal Category…)"
      // label as a code: that fallback wrapped 41 production goals up to six
      // layers deep, one layer per republish (see catalogue/category-code.ts).
      const { code: categoryCode, display: categoryDisplay } = categoryForWrite(
        obj, (c) => state.goalCategories.get(c)?.display);
      const subcategoryCode = obj.subcategoryCode || obj.subcategory;
      const subcategoryDisplay = (obj.categoryCode && obj.subcategoryCode)
        ? state.goalSubcategories.get(`${obj.categoryCode}__${obj.subcategoryCode}`)?.display || obj.subcategory
        : obj.subcategory;

      batch.set(goalRef, {
        resourceType: "Goal",
        lifecycleStatus: "active",
        // OMON-183: mandatory-per-shift applies to goals too; plain boolean,
        // always written (same semantics as the _monitoredEvents entries).
        // ADR-0035 am.8: the role set, always; `_mandatory` its derived shadow.
        ...roleFields(obj, defOf(obj.name)),
        achievementStatus: {
          coding: [{
            system: "http://terminology.hl7.org/CodeSystem/goal-achievement",
            code: obj.currentScore > 0 ? "in-progress" : "no-change",
            display: obj.currentScore > 0 ? "In Progress" : "No Change",
          }],
        },
        priority: {
          coding: [{
            system: "http://terminology.hl7.org/CodeSystem/goal-priority",
            code: "medium-priority",
            display: "Medium Priority",
          }],
        },
        description: {
          coding: [{
            system: "https://sweettreeconnect.com/fhir/CodeSystem/goal",
            code: obj.name,
            display: obj.title,
          }],
          text: obj.title,
        },
        _descriptionText: obj.title,
        // OMON-160: subject points at the EpisodeOfCare, not the Patient/Person.
        // The Firestore Security Rules' userOnSubjectEocData helper does
        // get(data.subject.reference) and expects an EoC (with careTeam[0])
        // back. A Person ref breaks that traversal because Person docs have
        // no careTeam field. FHIR-canonical would be subject -> Patient, but
        // the rule's traversal model requires subject -> EoC. The patient
        // identity is preserved at eoc.subject[0].reference for the readers
        // that need it (see client-context.ts:215).
        subject: { reference: eocRef },
        // _subjectId denorm retired in Step D of the data-model-drift audit
        // (architecture/audits/2026-05-21-omon-data-model-drift.md). Zero
        // readers in the codebase; canonical `subject.reference` already
        // carries the same identity.
        category: [
          {
            coding: [{
              system: "https://sweettreeconnect.com/fhir/CodeSystem/goal-category",
              code: categoryCode,
              display: categoryDisplay,
            }],
          },
          {
            coding: [{
              system: "https://sweettreeconnect.com/fhir/CodeSystem/goal-subcategory",
              code: subcategoryCode,
              display: subcategoryDisplay,
            }],
          },
        ],
        // OMON-033 (Goal.kind): the four-axes classifier for *why* the goal
        // exists — kept separate from `_measurementType` (how recorded) and
        // `categoryCode` (which SOMA domain). This writer authors
        // outcome-monitoring SOMA goals only, so kind is always
        // "developmental"; monitoring/care-need/safety goals come from
        // full-support-plan authoring. Legacy goals backfilled by
        // scripts/backfill_goal_kind.py (OMON-033 step-5).
        kind: "developmental",
        target: [{
          detailInteger: obj.targetScore,
          dueDate: goalEndDateStr(obj),
          measure: {
            coding: [{
              system: "https://sweettreeconnect.com/fhir/CodeSystem/goal",
              code: obj.name,
              display: obj.title,
            }],
          },
        }],
        startDate: goalStartDateStr(obj),
        continuous: true,
        _currentScore: obj.currentScore,
        _baselineMonths: obj.baselineMonths,
        _baselineFixed: obj.currentScore > 0,
        _trajectory: obj.trajectory,
        _targets: obj.trajectory.map((score: number, i: number) => ({
          date: monthOffsetToDate(i + (obj.goalStartMonth || 0)),
          score,
          setBy: null,
        })),
        _smartGoal: obj.smartGoal,
        // OMON-135: persist per-plan carer-guidance override only when
        // it actually differs from the current canonical
        // (`_activityDefinition.documentation[]`). Equal-to-canonical ⇒
        // absent on disk ⇒ readers late-bind to canonical
        // (`feedback_late_binding_default`). The "Reset to canonical"
        // affordance resets the builder state to canonical, which
        // then yields equality here and the field stays absent.
        ...(carerGuidanceIsOverride(obj)
          ? { _carerGuidance: obj.carerGuidance }
          : {}),
        // Ed 2026-09-29: the plan's one-text override, only when it differs
        // from the catalogue's. The legacy `_carerGuidance` above is still
        // written unchanged so a carer app that only reads it keeps this
        // person's text until every carer build reads `_carerGuidanceText`.
        ...planGuidanceTextField(obj),
        _order: orderByIndex[bi],
        statusDate: goalStartDateStr(obj),
        note: [],
        _sourceApplication: "outcomemonitoring_web",
        // OMON-143: stamp meta.security from the planner's dataScope claim.
        // `test` → HTEST label; `real`/`both`/nil → empty array (explicit
        // "not test" stamp so rules can reliably `hasAny([HTEST])`).
        meta: {
          source: "https://sweettreeconnect.com/fhir/ActorDefinition/outcomemonitoring_web",
          security: metaSecurityFor(state.authClaims),
        },
      });
    }

    // Create CarePlan document (ref allocated at batch start for OMON-032)
    batch.set(carePlanRef, {
      resourceType: "CarePlan",
      status: "active",
      intent: "plan",
      title: `${state.selectedPatient.name} -- Outcome Monitoring`,
      period: {
        start: state.planStartDate,
        end: monthOffsetToDate(state.planMonths),
      },
      category: [{
        coding: [{
          system: "https://sweettreeconnect.com/fhir/careplan-category",
          code: "outcome-monitoring",
          display: "Outcome Monitoring",
        }],
      }],
      // OMON-160: subject points at the EpisodeOfCare; see plan-builder.ts
      // Goal write site above for the full rationale.
      subject: { reference: eocRef },
      // FHIR R5 stewardship. ☠️ There is NO `CarePlan.author` in R5 — that was
      // R4, and R5 split it into `contributor[]` (who provided the content)
      // and `custodian` (0..1, "the care plan is attributed to the
      // custodian"). Ed, 2026-09-25: a carer's "Notify office" escalation
      // reaches the custodian — OneTouch can't express team structure (most
      // office staff are just area:office/workgroup:office), but who is
      // answerable for a plan is a fact about OUR data.
      //
      // Each save allocates a NEW version document (`_version` /
      // `_supersedes` below), so the saver becomes custodian by default —
      // Ed's "latest author is the recipient" rule — while `contributor[]`
      // accumulates everyone who has saved. The settings bar's "Escalations
      // to" picker overrides that default and marks the result
      // `_custodianPinned`, so a colleague fixing a typo never silently
      // inherits responsibility. Rule + edges: ./plan-custodian.
      //
      // ☠️ `state.builderCustodian` is passed on EVERY save, `null` included:
      // it is seeded from the live plan's pin when the builder opens, so
      // passing it is how a pin survives — and `null` is how clearing one
      // takes effect. Omitting the argument would make unpinning impossible.
      ...planStewardshipFields(
        state.authClaims?.personDocId,
        (id) => doc(db, "_person", id),
        mostRecentPriorData,
        state.builderCustodian,
      ),
      ...(encounterRef ? { encounter: { reference: encounterRef } } : {}),
      ...(careTeamRef ? { careTeam: [{ reference: careTeamRef }] } : {}),
      goal: goalRefs.map((ref) => ({ reference: ref })),
      // OMON-119 FULL: write the planner-authored monitored events as a
      // Reference array on the CarePlan. Per ADR-0015 the entries carry
      // just `{___use, reference}` — the migrated entries from OMON-134
      // additionally have `dueDate`/`targetMeasure`/`_migratedFromGoal`,
      // but those are migration-time-only metadata; new planner entries
      // don't carry them. Empty array if no monitored events selected.
      _monitoredEvents: state.builderMonitoredEvents.map((m) => ({
        ___use: "Monitored event Reference (FHIR-aligned per ADR-0015)",
        reference: doc(db, "_observationDefinition", m.odCode),
        // OMON-174: snapshot the prompting text onto the entry at save time
        // (freeze-at-add-time, like _goal._smartGoal — Ed 2026-06-10). Written
        // even when untouched so the versioned plan shows what was approved;
        // omitted only when there's no text at all (read-side then falls back
        // to the live canonical).
        ...(m.prompt ? { _prompt: m.prompt } : {}),
        // OMON-232: freeze the per-part prompts the same way (only the fact's
        // current parts, only non-empty text; blank → reader uses the default).
        ...partPromptsField(m),
        // OMON-179/177: freeze the per-shift policy values. Fields are written
        // only for affordances the CURRENT definition grants; explicit false =
        // planner deactivated a granted control for this client.
        ...monitoredEntryPolicyFields(m, state.catalogueData.get(m.odCode)),
      })),
      created: new Date().toISOString(),
      // Step B5 (data-model-drift audit 2026-05-21): no _episodeOfCareId
      // denorm write. The plan→EoC relationship lives canonically on
      // _episodeofcare.carePlan[] (updated below via batch.update).
      _version: newVersion,
      // OMON-045: forward `_supersedes` pointer on the new doc. Conditional
      // spread because Journey 1 (first plan, no prior) has nothing to point
      // at. Mirrors FHIR R5's `CarePlan.replaces[]` convention (singular here
      // because we maintain at-most-one active per EoC after OMON-032's fix).
      ...(mostRecentPriorRef ? { _supersedes: { reference: mostRecentPriorRef } } : {}),
      _sourceApplication: "outcomemonitoring_web",
      // OMON-143: stamp meta.security from the planner's dataScope claim.
      meta: {
        source: "https://sweettreeconnect.com/fhir/ActorDefinition/outcomemonitoring_web",
        security: metaSecurityFor(state.authClaims),
      },
    });

    // Update parent EpisodeOfCare to include the new CarePlan in its
    // canonical `carePlan[]` Reference array (Pattern A — FHIR R5
    // EpisodeOfCare.carePlan). This is now the SOLE source of truth
    // for plan↔EoC linkage; the `_episodeOfCareId` denorm write was
    // retired in Step B5 of the data-model-drift audit
    // (architecture/audits/2026-05-21-omon-data-model-drift.md).
    //
    // arrayUnion appends without dedup-checking deep equality — but each
    // plan save allocates a fresh carePlanRef so the entry is always
    // unique. Superseded plans stay in carePlan[] (FHIR keeps history).
    batch.update(eocRef, {
      carePlan: arrayUnion({
        ___use: "The plans of care that apply to this episode (FHIR R5 EpisodeOfCare.carePlan)",
        reference: carePlanRef,
      }),
    });

    // Update Encounter with basedOn reference to this CarePlan (FHIR bridge)
    if (encounterRef) {
      const encSnap = await getDoc(encounterRef);
      const encData = encSnap.data();
      const currentBasedOn = Array.isArray(encData?.basedOn)
        ? encData.basedOn.filter((e: any) => e?.reference instanceof DocumentReference)
        : [];
      currentBasedOn.push({ reference: carePlanRef, active: true });
      batch.update(encounterRef, { basedOn: currentBasedOn });
    }

    await batch.commit();

    const meCount = state.builderMonitoredEvents.length;
    const meSuffix = meCount > 0 ? ` + ${meCount} monitored event${meCount === 1 ? "" : "s"}` : "";
    statusEl.textContent = `Saved SOMA plan with ${state.builderGoals.length} goals${meSuffix}`;
    state.builderMode = "view";
    state.builderGoals = [];
    state.builderMonitoredEvents = [];

    // Update local state
    state.selectedPatient.hasPlan = true;
    await loadPatientPlan(state.selectedPatient);
    renderClientsView();
  } catch (err) {
    console.error("Failed to save plan:", err);
    saveBtn.disabled = false;
    saveBtn.textContent = `Save Plan (${state.builderGoals.length})`;
    statusEl.textContent = "Error saving plan -- check console";
  }
}

// ======================================================================
// OMON-119 FULL — Monitored event helpers
// ======================================================================

/**
 * Render the "Monitored events" section beneath the developmental goal
 * list in the plan-builder. Each pending entry shows the title, the
 * subcategory chip (if known), and a × remove button. Renders nothing
 * when no monitored events are pending.
 */
function renderBuilderMonitoredEventsSection(): string {
  if (state.builderMonitoredEvents.length === 0) return "";
  // OMON-179/177: each row is the per-shift-obligation editor. Policy
  // controls are AFFORDANCE-GATED against the current definition (only
  // librarian-granted flags render) and seeded values freeze onto the plan
  // entry at publish. The grey "librarian default" / amber "edited" chips
  // show seeded-vs-tuned state per control.
  const polLine = (inner: string) =>
    `<div style="display:flex;align-items:center;gap:8px;padding:8px 2px;border-top:1px solid #f3f4f6;font-size:13px;">${inner}</div>`;
  const polSwitch = (kind: string, i: number, on: boolean) =>
    `<span data-me-pol="${kind}" data-me-pol-idx="${i}" role="switch" aria-checked="${on}" title="${on ? "On" : "Off"}" style="flex:0 0 auto;width:38px;height:21px;border-radius:11px;cursor:pointer;user-select:none;position:relative;background:${on ? "#16a34a" : "#d1d5db"};transition:background .15s;">
      <span style="position:absolute;top:2px;left:${on ? "19px" : "2px"};width:17px;height:17px;border-radius:50%;background:#fff;transition:left .15s;box-shadow:0 1px 2px rgba(0,0,0,.25);"></span>
    </span>`;
  const seededChip = `<span style="font-size:11px;color:#6b7280;background:#f3f4f6;border-radius:5px;padding:1px 6px;margin-left:6px;">librarian default</span>`;
  const rows = state.builderMonitoredEvents.map((m, i) => {
    const od = state.catalogueData.get(m.odCode);
    // OMON-183 P1: defaults, not gates — both toggles render on EVERY row,
    // seeded from the librarian's OD flags. The chip goes amber and names the
    // librarian default whenever the planner has deviated from it.
    const def = defaultsFromOd(od);
    const defThr = typeof od?._escalationThresholdDefault === "number"
        ? od._escalationThresholdDefault
        : DEFAULT_ESCALATION_THRESHOLD;
    const defChip = (isDefault: boolean, defOn: boolean) => isDefault
      ? seededChip
      : `<span style="font-size:11px;color:#92400e;background:#fffbeb;border:1px solid #fde68a;border-radius:5px;padding:1px 6px;margin-left:6px;font-weight:700;">librarian default: ${defOn ? "on" : "off"}</span>`;
    // OMON-232 (Ed, 2026-10-01): an incident (ABC chart) has no target and no
    // escalation threshold — escalating means THIS incident.
    const incident = isIncidentKind(od?._recordingKind);
    const parts = partsOf(od);
    // OMON-183 P2: target lives inline on the recording line, not in policy.
    const targetHtml = incident ? "" : m.targetOn === true
      ? `<span style="display:inline-flex;align-items:center;gap:4px;background:#fff;border:1px solid #d1d5db;border-radius:7px;padding:1px 8px;color:#374151;">target ≤ <input type="number" data-me-pol-num="targetValue" data-me-pol-idx="${i}" value="${typeof m.targetValue === "number" ? m.targetValue : ""}" placeholder="—" style="width:36px;font-size:13px;border:none;border-bottom:1px solid #d1d5db;text-align:center;outline:none;background:transparent;"> per day <span data-me-pol="targetOn" data-me-pol-idx="${i}" title="Remove target" style="color:#9ca3af;cursor:pointer;">✕</span></span>`
      : `<span data-me-pol="targetOn" data-me-pol-idx="${i}" style="color:#1e40af;cursor:pointer;font-weight:600;">+ add target</span>`;
    let policyHtml = "";
    policyHtml += polLine(renderRolesControl("me", i, m, od as unknown as Record<string, unknown> | undefined, "completing this"));
    // ADR-0035 am.9a: the planner allows "Not applicable on this shift" per
    // item. Off by default; saved as `_allowNotApplicable` only when ticked.
    policyHtml += polLine(`<label style="flex:1;display:flex;align-items:flex-start;gap:8px;cursor:pointer;"><input type="checkbox" data-me-allow-na-idx="${i}"${m.allowNotApplicable === true ? " checked" : ""} style="margin-top:3px;"><span><b style="font-weight:600;">${escapeHtml(ALLOW_NOT_APPLICABLE_LABEL)}</b><div style="font-size:11px;color:#6b7280;">${escapeHtml(ALLOW_NOT_APPLICABLE_HELP)}</div></span></label>`);
    {
      // Direction is part of "is this the default?": a fact flipped to
      // "below" is a deliberate planner decision, and the chip must not claim
      // otherwise.
      const dirIsDefault = (m.escalationDirection ?? DEFAULT_ESCALATION_DIRECTION) === DEFAULT_ESCALATION_DIRECTION;
      const isDefault = m.escalate === def.escalatable
        && (incident || m.escalate !== true || (m.escalationThreshold === defThr && dirIsDefault));
      const chip = defChip(isDefault, def.escalatable);
      // ☠️ above | below (Ed, 2026-09-02). Some facts concern us when they
      // DROP — fluid intake, meals eaten, hours slept — and telling a carer
      // "notifies office above 2" for one of those is worse than saying
      // nothing: they would record a falling number and move on.
      const dirSel = `<select data-me-pol-dir="escalationDirection" data-me-pol-idx="${i}" ${m.escalate === true ? "" : "disabled"} style="padding:3px 4px;border:1px solid #d1d5db;border-radius:7px;font-size:13px;">`
        + `<option value="above"${(m.escalationDirection ?? "above") === "above" ? " selected" : ""}>above</option>`
        + `<option value="below"${m.escalationDirection === "below" ? " selected" : ""}>below</option>`
        + `</select>`;
      if (incident) {
        policyHtml += polLine(`<span style="flex:1;"><b style="font-weight:600;">Escalate</b> — carer can notify office ${chip}<div style="font-size:11px;color:#6b7280;">Carers can notify the office about an incident</div></span>${polSwitch("escalate", i, m.escalate === true)}`);
      } else {
        policyHtml += polLine(`<span style="flex:1;"><b style="font-weight:600;">Escalate</b> — carer can notify office ${chip}<div style="font-size:11px;color:#6b7280;">Carers see a Notify office tickbox. Nothing is sent automatically; the carer decides. The number is their guide and a line on the chart.</div></span>${dirSel}<input type="number" data-me-pol-num="escalationThreshold" data-me-pol-idx="${i}" value="${typeof m.escalationThreshold === "number" ? m.escalationThreshold : ""}" placeholder="—" ${m.escalate === true ? "" : "disabled"} style="width:52px;padding:3px 6px;text-align:center;border:1px solid #d1d5db;border-radius:7px;">${polSwitch("escalate", i, m.escalate === true)}`);
      }
    }
    const policySection = policyHtml
      ? `<div style="font-size:11px;font-weight:700;letter-spacing:.05em;text-transform:uppercase;color:#374151;margin:12px 0 2px;">This plan — seeded from librarian defaults, tuned per client</div>${policyHtml}`
      : "";
    return `
    <div class="card builder-row" data-builder-me-idx="${i}" style="border-left:3px solid #d97706;">
      <span class="drag-handle" title="Drag to reorder" aria-label="Drag to reorder">&#x283F;</span>
      <div class="builder-row-content">
        <div class="top-row">
          <div class="info">
            <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;">
              ${m.subcategory ? `<span class="category-chip" style="background:#fffbeb;color:#92400e;padding:2px 8px;border-radius:10px;font-size:13px;font-weight:500;">${escapeHtml(m.subcategory)}</span>` : ""}
              <div class="title">${escapeHtml(m.title)}</div>
            </div>
            <div class="meta" style="color:#6b7280;font-size:13px;margin-top:2px;display:flex;align-items:center;gap:6px;flex-wrap:wrap;">Monitored event · per-shift recording (${escapeHtml(recordingKindShortLabel(od))})${targetHtml}</div>
          </div>
          ${requiredMonitoredOdCodes(state.catalogueData).has(m.odCode)
            ? `<span class="meta">Required on every plan</span>`
            : `<button class="btn-outline" data-remove-monitored="${escapeHtml(m.odCode)}" style="padding:4px 8px;align-self:start;">&times;</button>`}
        </div>
        <div class="smart-goal-area">
          <label>Monitored Event Prompt</label>
          <textarea class="smart-goal-textarea" rows="2" data-me-prompt-idx="${i}"
            placeholder="What should the carer know and do when handling this monitored event?"
          >${escapeHtml(m.prompt || "")}</textarea>
        </div>
        ${parts.map((p) => `
        <div class="smart-goal-area">
          <label>${escapeHtml(p.label)}</label>
          <textarea class="smart-goal-textarea" rows="2" data-me-part-idx="${i}" data-me-part-code="${escapeHtml(p.code)}"
            placeholder="What should the carer write here?"
          >${escapeHtml(resolvePartPrompt(p, m.componentPrompts))}</textarea>
        </div>`).join("")}
        ${policySection}
      </div>
    </div>
  `;
  }).join("");
  return `
    <div id="builderMonitoredEventList" style="display:flex;flex-direction:column;gap:8px;">${rows}</div>
  `;
}

/**
 * OMON-121 follow-up: tab strip in the plan builder mirroring the
 * view-mode tab strip in detail.ts. Re-uses `state.detailActiveTab` so
 * the planner's tab choice survives view ↔ edit transitions.
 */
/**
 * "Office contact" in the plan-settings bar — the FHIR R5 `custodian`.
 *
 * Deliberately stated as a consequence ("escalations go to …") rather than as
 * a field name: the planner is choosing who gets woken up, not editing FHIR.
 * The unpinned case names the actual default rather than showing a blank, so
 * an unset contact never reads as "nobody will hear about this".
 */
/**
 * Person ids whose name we have already tried to resolve. Prevents a
 * re-render loop when a pinned custodian is no longer in the office
 * directory (their role was revoked), where the lookup legitimately
 * returns null every time.
 */
const nameLookupsAttempted = new Set<string>();

function renderOfficeContactSetting(): string {
  const pinned = state.builderCustodian;
  const label = pinned
    ? escapeHtml(pinned.name ?? "a colleague")
    : "whoever saves this plan";
  return `
    <label class="plan-setting">Escalations to
      <button class="dur-btn" id="officeContactBtn"
        style="width:auto;white-space:nowrap;padding:2px 8px;font-weight:500;"
        title="Who a carer's “Notify office” reaches for this plan. support@ is always copied."
      >${label}${pinned ? "" : " &middot; default"}</button>
    </label>`;
}

function renderBuilderTabStrip(): string {
  // OMON-183 P3: the tabs are gone — one view, both worlds visible. This is
  // now just the counts line under the plan header.
  const goalCount = state.builderGoals.length;
  const meCount = state.builderMonitoredEvents.length;
  return `
    <div style="font-size:13px;color:#6b7280;margin:8px 0 12px 0;">
      ${goalCount} developmental goal${goalCount === 1 ? "" : "s"} · ${meCount} monitored event${meCount === 1 ? "" : "s"}
    </div>
  `;
}

/**
 * OMON-121 follow-up: conditional content for the active builder tab.
 * - Developmental tab: "Add Goals..." button + the existing goal-row list
 *   (`objHtml` is computed before this helper runs).
 * - Monitored events tab: "Add Monitored Events..." button + the
 *   pending-monitored-events list (or empty state if none yet).
 *
 * Only the active tab's "Add..." button is shown — keeps the two
 * authoring flows visually separated per Ed's "logically separate"
 * framing (2026-05-22).
 */
/**
 * OMON-183 P4: the Shift preview lens — the draft rendered as the carer iOS
 * shift view will order it: MANDATORY items first, then the rest. Read-only
 * (authoring happens in the Author lens); per-item line mirrors the carer
 * card essentials: title, recording method, prompt/SMART snippet, badges.
 */
/** OMON-358: which role the shift preview is showing (off production only). */
let previewRole: ShiftRoleCode = "primary";

function renderShiftPreview(): string {
  type PreviewItem = { title: string; kind: string; text: string; mandatory: boolean; escalate: boolean; isGoal: boolean };
  const rolesOn = carersChooseRoles(appEnv);
  // Where carers choose a role, "must complete" is what THIS role owes; on
  // production, owed by any role (carers can't choose a role there yet). am.8.
  const owes = (item: RoleBearing, def: Record<string, unknown> | undefined) => {
    const roles = itemRoles(item, def);
    return rolesOn ? roles.includes(previewRole) : roles.length > 0;
  };
  const items: PreviewItem[] = [
    ...state.builderMonitoredEvents.map((m) => ({
      title: m.title,
      kind: recordingKindShortLabel(state.catalogueData.get(m.odCode)),
      text: m.prompt || "",
      // Off production, "must complete" is what THIS role owes (am.7c).
      mandatory: owes(m, defOf(m.odCode)),
      escalate: m.escalate === true,
      isGoal: false,
    })),
    ...state.builderGoals.map((g) => ({
      title: g.title,
      kind: "1–5 support scale",
      text: g.smartGoal || "",
      mandatory: owes(g, defOf(g.name)),
      escalate: false,
      isGoal: true,
    })),
  ];
  const mandatory = items.filter((i) => i.mandatory);
  const optional = items.filter((i) => !i.mandatory);
  const card = (i: PreviewItem) => `
    <div style="border:1px solid #e5e7eb;border-radius:10px;padding:8px 12px;margin-bottom:8px;background:#fff;">
      <div style="display:flex;align-items:center;gap:8px;">
        <b style="font-weight:650;font-size:13px;flex:1;">${escapeHtml(i.title)}</b>
        ${i.mandatory ? `<span style="font-size:11px;font-weight:700;border-radius:5px;padding:1px 8px;background:#fef2f2;color:#991b1b;">MANDATORY THIS SHIFT</span>` : ""}
        ${i.escalate ? `<span style="font-size:11px;font-weight:700;border-radius:5px;padding:1px 8px;background:#fffbeb;color:#92400e;">carer can notify office</span>` : ""}
        <span style="font-size:11px;color:#6b7280;">${escapeHtml(i.kind)}</span>
      </div>
      ${i.text ? `<div style="font-size:13px;color:#374151;margin-top:4px;line-height:1.45;">${escapeHtml(i.text)}</div>` : ""}
    </div>`;
  const section = (label: string, list: PreviewItem[]) => list.length
    ? `<div style="font-size:11px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:#991b1b;margin:16px 0 8px;">${label} · ${list.length}</div>${list.map(card).join("")}`
    : "";
  const rolePicker = rolesOn
    ? `<div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin:8px 0 4px;"><span style="font-size:13px;color:#374151;font-weight:600;">Preview as</span>${SHIFT_ROLES.map((r) =>
        `<button type="button" data-preview-role="${r.code}" style="font-size:13px;border-radius:999px;padding:4px 12px;cursor:pointer;border:1px solid ${r.code === previewRole ? "#9d0a6d" : "#d1d5db"};background:${r.code === previewRole ? "#9d0a6d" : "#fff"};color:${r.code === previewRole ? "#fff" : "#374151"};">${escapeHtml(r.display)}</button>`).join("")}</div>`
    : "";
  return `
    <div style="font-size:13px;color:#6b7280;margin:4px 0 4px;">How a carer's shift view will order this plan — mandatory obligations first. Switch back to Draft to make changes.</div>
    ${rolePicker}
    ${section("Must complete this shift", mandatory)}
    ${section("Also being tracked", optional)}
    ${items.length === 0 ? `<div class="empty-state" style="padding:24px;"><p>Nothing on this plan yet.</p></div>` : ""}
  `;
}

function renderBuilderTabContent(objHtml: string): string {
  // OMON-183 P3: one view, one add path. Both worlds render stacked with
  // their own section headings; a single "+ Add from catalogue" opens the
  // unified capability-routed picker.
  const meBody = state.builderMonitoredEvents.length === 0
    ? ""
    : `<div style="font-size:11px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:#991b1b;margin:16px 0 8px;">Monitored events · ${state.builderMonitoredEvents.length}</div>
       ${renderBuilderMonitoredEventsSection()}`;
  const goalsBody = state.builderGoals.length === 0
    ? ""
    : `<div style="font-size:11px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:#991b1b;margin:16px 0 8px;">Developmental goals · ${state.builderGoals.length}</div>
       ${objHtml}`;
  const empty = state.builderGoals.length === 0 && state.builderMonitoredEvents.length === 0
    ? `<div class="empty-state" style="padding:24px;">
        <h3>Nothing on this plan yet</h3>
        <p>Add facts from the catalogue — each renders its own world (goals get a "what does good look like", monitored events get prompts and policy).</p>
      </div>`
    : "";
  const lens = (key: "author" | "preview", label: string) => `
    <span data-builder-lens="${key}" style="padding:4px 12px;cursor:pointer;font-size:13px;${state.builderLens === key ? "background:#111827;color:#fff;font-weight:600;" : "color:#6b7280;"}">${label}</span>`;
  const lensBar = `
    <span style="display:inline-flex;border:1px solid #d1d5db;border-radius:8px;overflow:hidden;">${lens("author", "Draft")}${lens("preview", "Preview")}</span>`;
  if (state.builderLens === "preview") {
    return `
      <div style="display:flex;gap:8px;margin-bottom:8px;flex-wrap:wrap;align-items:center;">${lensBar}</div>
      ${renderShiftPreview()}
    `;
  }
  return `
    <div style="display:flex;gap:8px;margin-bottom:8px;flex-wrap:wrap;align-items:center;">${lensBar}
      <button class="btn-secondary" id="addFromCatalogue">+ Add from catalogue</button>
    </div>
    ${empty}${meBody}${goalsBody}
  `;
}

